import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/Checkbox';
import { extractErrorMessage } from '../../api/errors';
import { Discount } from '../../queries/discounts';
import { MarketingOffer, MarketingOfferDraft, useCreateMarketingOffer, useUpdateMarketingOffer } from '../../queries/marketingOffers';
import { SubscriptionPackage } from '../../queries/subscriptionPackages';

interface OfferFormDialogProps {
  offer: MarketingOffer | null; // null = create
  packages: SubscriptionPackage[];
  discounts: Discount[];
  onClose: () => void;
}

export function OfferFormDialog({ offer, packages, discounts, onClose }: OfferFormDialogProps) {
  const [draft, setDraft] = useState<MarketingOfferDraft>(
    offer
      ? {
          name: offer.name,
          description: offer.description,
          package_code: offer.package_code,
          offer_type: offer.offer_type,
          access_duration_days: offer.access_duration_days,
          discount_id: offer.discount_id,
          billing_cycle: offer.billing_cycle,
          is_active: offer.is_active,
        }
      : { offer_type: 'FREE_GRANT', is_active: true },
  );
  const [error, setError] = useState<string | null>(null);
  const create = useCreateMarketingOffer();
  const update = useUpdateMarketingOffer();
  const isPending = create.isPending || update.isPending;

  const isStripeDiscount = draft.offer_type === 'STRIPE_DISCOUNT';
  // A free grant hands out one specific package, so it must name one. A
  // Stripe discount is a coupon against whatever the customer buys, so
  // pinning it to a package (or a billing cycle) only narrows where it works.
  const canSave =
    draft.name && draft.offer_type && (isStripeDiscount ? !!draft.discount_id : !!draft.package_code);

  const handleSave = () => {
    setError(null);
    const onSettled = { onSuccess: onClose, onError: (err: unknown) => setError(extractErrorMessage(err, 'Save failed.')) };
    if (offer) {
      update.mutate({ ...draft, id: offer.id }, onSettled);
    } else {
      create.mutate(draft, onSettled);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{offer ? 'Edit offer' : 'Create offer'}</Dialog.Title>

          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor="offer-name" className="mb-1 block text-sm font-medium">Name</label>
              <input
                id="offer-name"
                value={draft.name ?? ''}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="offer-description" className="mb-1 block text-sm font-medium">Description</label>
              <textarea
                id="offer-description"
                value={draft.description ?? ''}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="offer-package" className="mb-1 block text-sm font-medium">
                Package {isStripeDiscount && <span className="font-normal text-muted-foreground">(optional)</span>}
              </label>
              <select
                id="offer-package"
                value={draft.package_code ?? ''}
                onChange={(e) => setDraft({ ...draft, package_code: e.target.value || null })}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="">{isStripeDiscount ? 'Any package' : 'Select a package…'}</option>
                {packages.map((p) => (
                  <option key={p.package_code} value={p.package_code}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="offer-type" className="mb-1 block text-sm font-medium">Offer type</label>
              <select
                id="offer-type"
                value={draft.offer_type ?? 'FREE_GRANT'}
                onChange={(e) => setDraft({ ...draft, offer_type: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="FREE_GRANT">Free grant</option>
                <option value="STRIPE_DISCOUNT">Stripe discount</option>
              </select>
            </div>

            {isStripeDiscount ? (
              <>
                <div>
                  <label htmlFor="offer-discount" className="mb-1 block text-sm font-medium">Linked discount</label>
                  <select
                    id="offer-discount"
                    value={draft.discount_id ?? ''}
                    onChange={(e) => setDraft({ ...draft, discount_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  >
                    <option value="">Select a discount…</option>
                    {discounts.map((d) => (
                      <option key={d.id} value={d.id}>{d.name} ({d.discount_code})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="offer-billing-cycle" className="mb-1 block text-sm font-medium">
                    Billing cycle <span className="font-normal text-muted-foreground">(optional)</span>
                  </label>
                  <select
                    id="offer-billing-cycle"
                    value={draft.billing_cycle ?? ''}
                    onChange={(e) => setDraft({ ...draft, billing_cycle: e.target.value || null })}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  >
                    <option value="">Any billing cycle</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </>
            ) : (
              <div>
                <label htmlFor="offer-duration" className="mb-1 block text-sm font-medium">Access duration (days, blank = indefinite)</label>
                <input
                  id="offer-duration"
                  type="number"
                  min={1}
                  value={draft.access_duration_days ?? ''}
                  onChange={(e) => setDraft({ ...draft, access_duration_days: e.target.value ? Number(e.target.value) : null })}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                />
              </div>
            )}

            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.is_active ?? true}
                onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
              />
              Active
            </label>
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isPending || !canSave}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
